import { Flex, Grid } from '@chakra-ui/react';
import { capitalize } from 'lodash';
import router from 'next/router';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { Heading, Paragraph } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import {
  ChannelMetrics,
  EmptyState,
  MetricCard,
  MetricsInfoPopover,
  PlatformMetricsSource,
  isZeroOrUndefined,
} from '~/components/CreatorChannelMetrics';
import { FEATURE_FLAGS, PARTIALLY_SUPPORTED_PLATFORMS } from '~/constants';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { SupportedPlatforms } from '~/screens/CreatorAudienceDemographics';
import { selectTalentManager } from '~/selectors/talent-manager';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

interface Props {
  creatorPlatformMetrics: ChannelMetrics | undefined;
  platform: SupportedPlatforms;
  isBuilder?: boolean;
  selectedMetrics?: string[];
  platformMetricsSource: PlatformMetricsSource | undefined;
}

export const MetricsContainer: React.FC<Props> = ({
  creatorPlatformMetrics,
  platform,
  isBuilder,
  selectedMetrics,
  platformMetricsSource,
}) => {
  const isPerformancePredictionMetricsEnabled = useFeature(
    FEATURE_FLAGS.foamPerformancePredictionMetrics,
  ).isEnabled;

  const slug = router.query.creatorSlug as string;
  const { data: creator } = useGetCreatorProfile({ slug });
  const manager = useSelector(selectTalentManager);

  const verifiedCreator =
    creator?.platforms[platform]?.status === 'disconnected' ||
    creator?.platforms[platform]?.status === 'connected';

  const connectionStatus = creator?.platforms[platform]?.status;

  const isConnected = match(platform)
    .with(
      PARTIALLY_SUPPORTED_PLATFORMS.snapchat,
      () =>
        connectionStatus === 'connected' ||
        connectionStatus === 'disconnected' ||
        connectionStatus === 'off-platform',
    )
    .otherwise(
      () =>
        connectionStatus === 'connected' || connectionStatus === 'disconnected',
    );

  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator as Creator,
    userAccessData.data as MeResponse,
  );
  const metricsToShow = creatorPlatformMetrics?.[platform] ?? [];

  const isEmpty = useMemo(() => {
    return match(platform)
      .with(PARTIALLY_SUPPORTED_PLATFORMS.snapchat, () => {
        // Omit profile tier and loyalty score metrics for snapchat while checking whether the metrics are empty
        const snapMetrics = metricsToShow.filter(
          (metric) =>
            metric.title !== 'Profile Tier' && metric.title !== 'Loyalty Score', // because these values are hardcoded
        );

        return snapMetrics.every((metric) => isZeroOrUndefined(metric.value));
      })
      .otherwise(() => {
        return metricsToShow.every((metric) => isZeroOrUndefined(metric.value));
      });
  }, [metricsToShow, platform]);

  const demographicsSource = platformMetricsSource?.[platform];
  const isSTMPrediction =
    demographicsSource === 'stm-predictions' &&
    isPerformancePredictionMetricsEnabled;

  const isPredictedVisible =
    connectionStatus === 'off-platform' &&
    isSTMPrediction &&
    isPerformancePredictionMetricsEnabled;

  const showEmptyState = useMemo(
    () =>
      (!isConnected && !isPredictedVisible) || (isEmpty && !isPredictedVisible),
    [isConnected, isEmpty, isPredictedVisible],
  );

  if (showEmptyState) {
    const messageToShow =
      platform !== PARTIALLY_SUPPORTED_PLATFORMS.snapchat
        ? 'Detailed Talent & Channel metrics are not available because the Talent has not posted any recent content.'
        : undefined;

    return (
      <EmptyState
        isManager={shouldHaveAccessAsManagerComanagerAdmin}
        platform={platform}
        hasError={isBuilder || verifiedCreator}
        isBuilder={isBuilder}
        isSTMPredicted={isPerformancePredictionMetricsEnabled}
        {...(messageToShow ? { messageToShow } : {})}
      />
    );
  }

  // Temporary fix: Tittle mapping for deprecated metrics in Meta Graph API v22
  // TODO: Remove this (https://whalar.atlassian.net/browse/FOAM-13539) once BE updates the metric names (https://whalar.atlassian.net/browse/FOAM-13538)
  const titleMapping = {
    'Average Impressions': 'Average Views',
    'Average Reels Impressions': 'Average Reels Views',
    'Average Story Impressions': 'Average Story Views',
  };
  const filteredMetricsToShow = metricsToShow.filter((metric) => {
    if (isZeroOrUndefined(metric.value)) return false;
    // If isBuilder is false, render all metrics. Otherwise, check if the metric.title exists in selectedMetrics for the platform.
    if (!isBuilder) return true;

    const normalizedMetricTitle =
      titleMapping[metric.title as keyof typeof titleMapping] || metric.title;

    return selectedMetrics?.some((selected) => {
      const normalizedSelected =
        titleMapping[selected as keyof typeof titleMapping] || selected;
      return normalizedSelected === normalizedMetricTitle;
    });
  });

  return (
    <>
      {!isBuilder ? (
        <Flex justifyContent="space-between">
          <Heading size="sm">Talent & Channel metrics</Heading>
          <MetricsInfoPopover platform={platform} />
        </Flex>
      ) : null}

      <Grid
        templateColumns={`repeat(auto-fit, minmax(300px, ${
          filteredMetricsToShow.length < 3 ? '300px' : '1fr'
        }))`}
        gap={4}
      >
        {filteredMetricsToShow.map((metric) => (
          <MetricCard
            key={metric.title}
            metricTitle={metric.title}
            metricSubtitle={metric.metricSubtitle}
            metricValue={metric.value}
            metricTransformValue={metric.transformValue}
            isDisabled={metric.isDisabled}
          />
        ))}
      </Grid>

      {!isSTMPrediction ? (
        <Paragraph size="sm" color="primary.300 ">
          {`Verified metrics from ${capitalize(platform)}'s API`}
        </Paragraph>
      ) : (
        <Paragraph size="sm" color="primary.300 ">
          {`Estimated metrics based on similar creators`}
        </Paragraph>
      )}
    </>
  );
};
